import {
   FC,
   SyntheticEvent,
   useCallback,
   useEffect,
   useMemo,
   useRef,
   useState,
} from "react"
import classNames from "classnames"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router"
import AlertBar from "./AlertBar"
import Avatar from "./Avatar"
import TenantSelector from "./TenantSelector"
import { getTenantLogo } from "./utils"
import AlertModal, { AlertModalType } from "@/components/AlertModal"
import ClearAlertModal from "@/components/AlertModal/ClearAlert"
import { Icon, IconColor } from "@/components/shared/Icon"
import Menu from "@/components/shared/Menu"
import Spinner, { SpinnerSize } from "@/components/shared/Spinner"
import WearablesModal from "@/components/WearablesModal"
import { Wearable, WearableModalType } from "@/components/WearablesModal/types"
import {
   CellDetailOverviewRoutes,
   InmateDetailOverviewRoutes,
} from "@/constants/routes"
import useModal from "@/hooks/useModal"
import { useScannedWearable } from "@/hooks/useScannedWearable"
import { ScannerConsumerType } from "@/hooks/useScanner"
import { useWearablesModal } from "@/hooks/useWearablesModal"
import { AppDispatch, RootState } from "@/store"
import {
   alertsSelectors,
   fetchAlertsByLevel,
   resetAlerts,
} from "@/store/alertsSlice"
import { fetchFacility, selectTimezone } from "@/store/facilitiesSlice"
import { selectInmates } from "@/store/inmatesSlice"
import { setSwapFlowScannerActive } from "@/store/scannerSlice"
import { isConditionError } from "@/store/utils"
import {
   Alert,
   AlertThreshold,
   AlertVariant,
   AlertVitals,
   TypefaceVariant,
} from "@/types/alert"
import { AlertSystemConditionType } from "@/types/alertConfigurations"
import { Token } from "@/types/index"
import { LevelConfigurationItemMode } from "@/types/levelConfiguration"
import { prepareAlertModalData } from "@/utils/alerts"
import { normalizeMacAddress } from "@/utils/index"
import "./index.scss"

interface HeaderProps {
   showAlerts?: boolean
}

export interface AlertModalData {
   dateTime: string
   id: string
   systemConditionType: AlertSystemConditionType | null
   alertType: AlertModalType
   levels: { name: string; type: string }[]
   locationName: string
   locationType?: string
   locationId: string
   alertVariant: keyof typeof AlertVariant
   typefaceColor: keyof typeof TypefaceVariant
   alertName: string
   vitals: AlertVitals
   isCustom: boolean
   heartRateThreshold: AlertThreshold
   respiratoryRateThreshold: AlertThreshold
   smartwatchId?: string
   inmateId?: string
}

const Header: FC<HeaderProps> = ({ showAlerts = true }) => {
   const location = useLocation()
   const navigate = useNavigate()
   const dispatch = useDispatch<AppDispatch>()
   const [alertsData, setAlertsData] = useState<Alert[]>([])

   const isAnimating = showAlerts && alertsData.length > 0
   const prevAlertColorRef = useRef<string | null>(null)
   const {
      isSuperAdmin,
      loading,
      firstName,
      lastName,
      tenants,
      selectedTenantId,
   } = useSelector(({ userReducer }: RootState) => userReducer)
   const { facilityDetails, facilityLevelSelectors } = useSelector(
      ({ facilitiesReducer }: RootState) => facilitiesReducer
   )

   const {
      handleWearableDisconnect,
      handleModalWearableSave,
      handleModalScannedWearableSave,
   } = useWearablesModal()

   const inmates = useSelector(selectInmates)
   const {
      loading: loadingAlerts,
      error: alertsError,
      loadingClearAlert,
      loadingClearAlerts,
      // errorClearAlert,
      alertIdToClear,
   } = useSelector(({ alertsReducer }: RootState) => alertsReducer)
   const alerts = useSelector(alertsSelectors.selectAll)
   const timezone = useSelector(selectTimezone)
   const showTimezone = showAlerts && timezone !== null

   const facilityId = sessionStorage.getItem(Token.FACILITY_ID)
   const facilityName = facilityDetails?.name ?? null
   const { isOpen, openModal, closeModal } = useModal(false)
   const {
      isOpen: isClearOpen,
      openModal: openClearModal,
      closeModal: closeClearModal,
   } = useModal(false)
   const [alertModalData, setAlertModalData] = useState({} as AlertModalData)
   const [isMenuOpen, setIsMenuOpen] = useState(false)
   const anchorRef = useRef<HTMLButtonElement>(null)

   const [selectedInmateId, setSelectedInmateId] = useState<string | null>(null)

   const initialModalMode = selectedInmateId
      ? WearableModalType.OPTIONS
      : WearableModalType.SCAN

   const {
      isOpen: isWearableModalOpen,
      openModal: openWearableModal,
      closeModal: closeWearableModal,
   } = useModal()

   const isSwapFlowScannerActive =
      isWearableModalOpen && selectedInmateId !== null

   const {
      isScanning,
      scannedSensor,
      wearableModalMode,
      wearableOriginMode,
      swapTargetRef,
      swapTargetInmateId,
      isModalOpenRef,
      resetScanState,
      handleDisconnect,
   } = useScannedWearable({
      selectedInmateId,
      initialModalMode,
      consumerType: ScannerConsumerType.HEADER_SWAP,
      scannerEnabled: isSwapFlowScannerActive,
      openWearableModal,
      onScannedWearableSave: handleModalScannedWearableSave,
      onDisconnect: handleWearableDisconnect,
   })

   isModalOpenRef.current = isWearableModalOpen

   useEffect(() => {
      async function fetchFacilityData() {
         try {
            await dispatch(fetchFacility()).unwrap()
         } catch (error) {
            if (!isConditionError(error))
               console.error("Facility details fetch failed:", error)
         }
      }
      if (!facilityId) return
      fetchFacilityData()
   }, [dispatch, facilityId])

   useEffect(() => {
      if (!showAlerts) return
      let isMounted = true
      let scheduledTimeout: NodeJS.Timeout | undefined

      async function fetchAlertsData() {
         try {
            if (facilityId && facilityLevelSelectors.length > 0) {
               const lowestFacilityLevel = facilityLevelSelectors.findLast(
                  (level) => level.activeValue
               )
               if (lowestFacilityLevel) {
                  const hasCells = facilityDetails.levels?.some(
                     (level) =>
                        level.parents?.includes(
                           lowestFacilityLevel.activeValue
                        ) &&
                        level.type.mode === LevelConfigurationItemMode.PHYSICAL
                  )
                  const hasWearables = inmates.some(
                     (inmate) => inmate.sensor !== null
                  )
                  if (!hasCells && !hasWearables) return
                  await dispatch(
                     fetchAlertsByLevel(lowestFacilityLevel.activeValue)
                  ).unwrap()
               }
            }
         } catch (err) {
            if (isMounted) {
               console.error("Alerts data fetch failed:", err)
            }
         } finally {
            if (isMounted) scheduledTimeout = setTimeout(fetchAlertsData, 1000)
         }
      }

      fetchAlertsData()

      return () => {
         isMounted = false
         dispatch(resetAlerts())
         clearTimeout(scheduledTimeout)
      }
   }, [
      dispatch,
      facilityDetails.levels,
      facilityId,
      facilityLevelSelectors,
      inmates,
      showAlerts,
   ])

   useEffect(() => {
      if (!loadingAlerts && alerts) {
         setAlertsData(alerts)
      }
   }, [loadingAlerts, alerts])

   useEffect(() => {
      if (alertModalData.id) {
         openModal()
      }
   }, [alertModalData, openModal])

   useEffect(() => {
      dispatch(setSwapFlowScannerActive(isSwapFlowScannerActive))
   }, [dispatch, isSwapFlowScannerActive])

   useEffect(() => {
      return () => {
         dispatch(setSwapFlowScannerActive(false))
      }
   }, [dispatch])

   const handleAlertClick = (alert: Alert) => {
      setAlertModalData(prepareAlertModalData(alert))
   }

   const shadowVariantClassName = useMemo(() => {
      if (!alerts || !alerts.length)
         return prevAlertColorRef.current
            ? `--${AlertVariant[prevAlertColorRef.current as keyof typeof AlertVariant]}`
            : ""
      const currentColor = alerts[0].alertConfiguration.color.card.toLowerCase()
      if (prevAlertColorRef.current !== currentColor) {
         prevAlertColorRef.current = currentColor
      }
      return `--${AlertVariant[prevAlertColorRef.current as keyof typeof AlertVariant]}`
   }, [alerts])

   const handleToggle = () => {
      setIsMenuOpen((prevOpen) => !prevOpen)
   }

   const handleClose = (event: Event | SyntheticEvent) => {
      if (
         anchorRef.current &&
         anchorRef.current.contains(event.target as HTMLElement)
      ) {
         return
      }

      setIsMenuOpen(false)
   }

   const viewCell = (cellId: string) => {
      const isCellDetailSubpage = Object.values(CellDetailOverviewRoutes).some(
         (route) => location.pathname.startsWith(route.replace(":id", ""))
      )
      closeModal()

      if (!isCellDetailSubpage) {
         navigate(CellDetailOverviewRoutes.SUMMARY.replace(":id", cellId))
      }
   }

   const viewInmate = (inmateId: string) => {
      closeModal()
      navigate(InmateDetailOverviewRoutes.SUMMARY.replace(":id", inmateId))
   }

   const handleSwapDeviceClick = useCallback(
      (inmateId: string) => {
         const inmate = inmates.find((inmate) => inmate.id === inmateId)
         const wearableId = inmate?.sensor?.macAddress
            ? normalizeMacAddress(inmate.sensor.macAddress)
            : null

         if (!wearableId) return

         swapTargetRef.current = inmateId
         setSelectedInmateId(inmateId)
         openWearableModal()
      },
      [inmates, openWearableModal, swapTargetRef]
   )

   const handleCloseWearableModal = useCallback(() => {
      setSelectedInmateId(null)
      closeWearableModal()
      resetScanState()
   }, [closeWearableModal, resetScanState])

   const handleWearableSave = useCallback(
      (wearable: Wearable, inmateId?: string) => {
         if (!inmateId) return
         handleModalWearableSave(wearable, inmateId)
      },
      [handleModalWearableSave]
   )

   return (
      <>
         <header
            className={classNames("header", {
               "header--with-alerts": showAlerts,
               "header--with-timezone": showTimezone,
               "header--no-alert": !showAlerts,
               "header--animating": isAnimating,
               [`header${shadowVariantClassName}`]: isAnimating,
            })}
         >
            {showAlerts && (
               <AlertBar
                  loadingAlerts={loadingAlerts}
                  alertsError={alertsError}
                  alertsData={alertsData}
                  loadingClearAlert={loadingClearAlert}
                  loadingClearAlerts={loadingClearAlerts}
                  alertIdToClear={alertIdToClear}
                  facilityId={facilityId}
                  isSuperAdmin={isSuperAdmin}
                  handleAlertClick={handleAlertClick}
                  handleSwapDeviceClick={handleSwapDeviceClick}
               />
            )}
            {!showAlerts && (
               <>
                  <TenantSelector />
                  <img
                     className="header__banner"
                     src={getTenantLogo(tenants, selectedTenantId)}
                     alt="logoBanner"
                  />
               </>
            )}
            <span
               className={classNames("header__menu", {
                  "header__menu--with-shadow": !showAlerts,
               })}
               onClick={handleToggle}
               ref={anchorRef}
               id="composition-button"
               aria-controls={isMenuOpen ? "composition-menu" : undefined}
               aria-expanded={isMenuOpen ? "true" : undefined}
               aria-haspopup="true"
            >
               <span
                  className={classNames("header__user-info", {
                     "header__user-info--loading": loading,
                  })}
               >
                  {loading ? (
                     <Spinner size={SpinnerSize.SMALL} />
                  ) : (
                     <>
                        <Avatar
                           firstName={firstName}
                           lastName={lastName}
                        />
                        <span className="header__user-info-wrapper">
                           <div>
                              {firstName} {lastName}
                           </div>
                           {facilityName && (
                              <div className="header__facility-name">
                                 {facilityName}
                              </div>
                           )}
                        </span>
                     </>
                  )}
               </span>
               <Icon
                  name={isMenuOpen ? "arrowUp" : "arrowDown"}
                  size="8"
                  color={IconColor.COOL_GRAY}
               />
            </span>
            <Menu
               open={isMenuOpen}
               anchorRef={anchorRef}
               handleClose={handleClose}
            />
         </header>
         {showTimezone && (
            <div className="header-timezone">
               <Icon
                  name="timezone"
                  size="12"
                  color={IconColor.COOL_GRAY}
               />
               <span>
                  Time zone:{" "}
                  <span className="header-timezone__value">{timezone}</span>
               </span>
            </div>
         )}
         <AlertModal
            open={isOpen}
            {...alertModalData}
            type={
               alertModalData.alertType === AlertModalType.WEARABLE
                  ? AlertModalType.WEARABLE
                  : AlertModalType.CELL
            }
            onManage={() => {
               closeModal()
               openClearModal()
            }}
            onSwap={() => {
               closeModal()
               handleSwapDeviceClick(alertModalData.inmateId ?? "")
            }}
            onClose={() => closeModal()}
            onView={() =>
               alertModalData.alertType === AlertModalType.WEARABLE
                  ? viewInmate(alertModalData.inmateId ?? "")
                  : viewCell(alertModalData.locationId)
            }
         />
         {isClearOpen && (
            <ClearAlertModal
               open
               {...alertModalData}
               onClose={() => closeClearModal()}
            />
         )}
         {isWearableModalOpen && !isScanning && (
            <WearablesModal
               mode={wearableModalMode}
               originMode={wearableOriginMode}
               openModal={isWearableModalOpen}
               handleCloseModal={handleCloseWearableModal}
               inmateId={swapTargetInmateId ?? selectedInmateId}
               wearable={scannedSensor}
               onSave={handleWearableSave}
               onSaveScanned={(inmateId) =>
                  handleModalScannedWearableSave(inmateId, scannedSensor)
               }
               onClickDisconnect={(inmateId) => handleDisconnect(inmateId)}
            />
         )}
      </>
   )
}

export default Header
